import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FilterX } from "lucide-react";
import { useDispositions } from "@/hooks/useDispositions";

const CLASSES = ["0", "1", "2", "3", "4", "5", "6", "7", "8"];

/** Global dashboard filter bar — every widget respects these. */
export default function DashFilterBar({ filters, onChange }) {
  const { options: dispositions } = useDispositions();
  const [assignees, setAssignees] = useState([]);
  const [dropdowns, setDropdowns] = useState({ sector: [], employee_count: [] });

  useEffect(() => {
    api.get("/leads/assignees").then((r) => setAssignees(r.data.assignees || [])).catch(() => {});
    Promise.all([
      api.get("/lead-dropdowns", { params: { key: "sector" } }),
      api.get("/lead-dropdowns", { params: { key: "employee_count" } }),
    ]).then(([s, e]) => setDropdowns({
      sector: (s.data.items || []).filter((o) => o.active !== false),
      employee_count: (e.data.items || []).filter((o) => o.active !== false),
    })).catch(() => {});
  }, []);

  const set = (k, v) => onChange({ ...filters, [k]: v });
  const hasFilters = Object.values(filters).some((v) => v && v !== "all");

  const iso = (d) => {
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  };
  const preset = (from, to) => onChange({ ...filters, date_from: from, date_to: to });
  const presets = [
    { id: "today", label: "Today", run: () => { const t = iso(new Date()); preset(t, t); } },
    { id: "7d", label: "Last 7 days", run: () => {
        const now = new Date(); const from = new Date(now); from.setDate(now.getDate() - 6);
        preset(iso(from), iso(now)); } },
    { id: "month", label: "This month", run: () => {
        const now = new Date();
        preset(iso(new Date(now.getFullYear(), now.getMonth(), 1)), iso(now)); } },
  ];

  const sel = (key, placeholder, items, width = "w-[150px]") => (
    <Select value={filters[key] || "all"} onValueChange={(v) => set(key, v === "all" ? "" : v)}>
      <SelectTrigger className={`h-8 text-xs ${width}`} data-testid={`dash-filter-${key}`}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        <SelectItem value="all">{placeholder}</SelectItem>
        {items}
      </SelectContent>
    </Select>
  );

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="dash-filter-bar">
      <Select value={filters.date_mode || "upload"}
              onValueChange={(v) => set("date_mode", v === "upload" ? "" : v)}>
        <SelectTrigger className="h-8 text-xs w-[125px]" data-testid="dash-filter-date-mode"
                       title="What the date range applies to">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="upload">Upload date</SelectItem>
          <SelectItem value="activity">Activity date</SelectItem>
        </SelectContent>
      </Select>
      <Input type="date" value={filters.date_from || ""} onChange={(e) => set("date_from", e.target.value)}
             className="h-8 text-xs w-[140px]" data-testid="dash-filter-date-from" title="From date" />
      <Input type="date" value={filters.date_to || ""} onChange={(e) => set("date_to", e.target.value)}
             className="h-8 text-xs w-[140px]" data-testid="dash-filter-date-to" title="To date" />
      <div className="flex items-center gap-1">
        {presets.map((p) => (
          <Button key={p.id} variant="outline" size="sm" onClick={p.run}
                  className="h-8 px-2 text-[11px] text-slate-600"
                  data-testid={`dash-preset-${p.id}`}>
            {p.label}
          </Button>
        ))}
      </div>
      {sel("assignee", "All agents", [
        <SelectItem key="__unassigned__" value="__unassigned__">Unassigned</SelectItem>,
        ...assignees.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>),
      ], "w-[170px]")}
      {sel("classification", "All classes",
        CLASSES.map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>), "w-[130px]")}
      {sel("disposition", "All dispositions",
        dispositions.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: o.color || "#94A3B8" }} />
              {o.value === o.label ? o.label : `${o.value} · ${o.label}`}
            </span>
          </SelectItem>
        )), "w-[170px]")}
      {sel("sector", "All sectors",
        dropdowns.sector.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
      {sel("employee_count", "All sizes",
        dropdowns.employee_count.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>), "w-[130px]")}
      {hasFilters && (
        <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500"
                onClick={() => onChange({})} data-testid="dash-filter-clear">
          <FilterX className="w-3.5 h-3.5 mr-1" /> Clear
        </Button>
      )}
    </div>
  );
}
